<script setup lang="ts" generic="T">
/**
 * SnapPager: full-width pages you swipe between (workspace tabs, browser pages, question steps, carousels).
 *
 * Native CSS scroll-snap does the physics, so touch, trackpad and wheel all feel right and it costs nothing
 * per frame. `v-model` is the page index: set it to go there (smooth, instant under reduced motion); it
 * updates when a swipe settles, and `change` fires. Arrow keys page when the pager has focus. Optional dots.
 *
 *   <SnapPager v-model="page" :items="tabs" dots aria-label="Open items">
 *       <template #default="{ item, index, active }">…</template>
 *   </SnapPager>
 *
 * Pass `items` (any array) or a `count`. Pages are always mounted; keep heavy pages light while inactive. When
 * items can be inserted or removed (workspace tabs), pass `itemKey` so each page keeps its component state as
 * it moves to another index instead of re-rendering with a different item.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { prefersReducedMotion } from '@/motion/tokens'

const props = withDefaults(defineProps<{
    items?: readonly T[]
    /** Page count when there are no `items`. */
    count?: number
    dots?: boolean
    /** Space between pages, px. */
    gap?: number
    ariaLabel?: string
    /** Identity of an item: pages are keyed by it (default: by index). */
    itemKey?: (item: T) => string | number
}>(), { items: undefined, count: 0, dots: false, gap: 0, ariaLabel: undefined, itemKey: undefined })

const emit = defineEmits<{ change: [index: number] }>()
const index = defineModel<number>({ default: 0 })

defineSlots<{
    default(props: { item: T | undefined; index: number; active: boolean }): unknown
}>()

const track = ref<HTMLElement | null>(null)
const total = computed(() => props.items?.length ?? props.count)
const pages = computed(() => Array.from({ length: total.value }, (_, i) => i))
function pageKey(i: number): string | number {
    const item = props.items?.[i]
    return props.itemKey && item !== undefined ? props.itemKey(item) : i
}
/** Index the track is (or is scrolling) at; the model follows once a swipe settles. */
const live = ref(index.value)

let settleTimer: ReturnType<typeof setTimeout> | null = null
let programmatic: number | null = null
let ro: ResizeObserver | null = null

const clampIndex = (i: number) => Math.min(Math.max(0, i), Math.max(0, total.value - 1))

function pageWidth(): number {
    const el = track.value
    return el ? el.clientWidth + props.gap : 1
}

function scrollToIndex(i: number, smooth: boolean) {
    const el = track.value
    if (!el) return
    programmatic = i
    el.scrollTo({ left: i * pageWidth(), behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' })
}

function settle() {
    settleTimer = null
    const el = track.value
    if (!el) return
    const i = clampIndex(Math.round(el.scrollLeft / pageWidth()))
    programmatic = null
    live.value = i
    if (i !== index.value) {
        index.value = i
        emit('change', i)
    }
}

function onScroll() {
    const el = track.value
    if (!el) return
    live.value = clampIndex(Math.round(el.scrollLeft / pageWidth()))
    // `scrollend` where supported; otherwise a quiet 120ms.
    if (settleTimer) clearTimeout(settleTimer)
    settleTimer = setTimeout(settle, 120)
}

function onScrollEnd() {
    if (settleTimer) clearTimeout(settleTimer)
    settle()
}

function go(i: number) {
    const to = clampIndex(i)
    live.value = to
    scrollToIndex(to, true)
}

function onKeydown(e: KeyboardEvent) {
    // Only the focused track pages: keys from a field (or any control) inside a page are theirs.
    if (e.target !== e.currentTarget || e.defaultPrevented) return
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault()
        go(live.value + (e.key === 'ArrowRight' ? 1 : -1))
    } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault()
        go(e.key === 'Home' ? 0 : total.value - 1)
    }
}

watch(index, (i) => {
    const to = clampIndex(i)
    if (to === live.value && programmatic === null) return
    if (programmatic === to) return
    live.value = to
    scrollToIndex(to, true)
})

watch(total, () => {
    if (index.value > total.value - 1) index.value = clampIndex(index.value)
})

onMounted(() => {
    scrollToIndex(clampIndex(index.value), false)
    programmatic = null
    track.value?.addEventListener('scrollend', onScrollEnd)
    // Keep the current page aligned when the pager resizes (rotation, sheet detent change).
    if (typeof ResizeObserver !== 'undefined' && track.value) {
        ro = new ResizeObserver(() => scrollToIndex(live.value, false))
        ro.observe(track.value)
    }
})

onBeforeUnmount(() => {
    if (settleTimer) clearTimeout(settleTimer)
    track.value?.removeEventListener('scrollend', onScrollEnd)
    ro?.disconnect()
})

defineExpose({ go })
</script>

<template>
    <div class="sp" :style="{ '--sp-gap': `${gap}px` }">
        <div
            ref="track"
            class="sp-track"
            role="group"
            aria-roledescription="carousel"
            :aria-label="ariaLabel"
            tabindex="0"
            @scroll.passive="onScroll"
            @keydown="onKeydown"
        >
            <div
                v-for="i in pages"
                :key="pageKey(i)"
                class="sp-page"
                role="group"
                aria-roledescription="page"
                :aria-label="`${i + 1} of ${total}`"
                :aria-hidden="i !== live ? 'true' : undefined"
                :inert="i !== live || undefined"
            >
                <slot :item="items?.[i]" :index="i" :active="i === live" />
            </div>
        </div>
        <div v-if="dots && total > 1" class="sp-dots">
            <button
                v-for="i in pages"
                :key="i"
                type="button"
                class="sp-dot"
                :class="{ 'is-on': i === live }"
                :aria-label="`Page ${i + 1}`"
                :aria-current="i === live ? 'true' : undefined"
                @click="go(i)"
            >
                <span />
            </button>
        </div>
    </div>
</template>

<style scoped>
.sp {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
}
.sp-track {
    display: flex;
    flex: 1 1 auto;
    gap: var(--sp-gap);
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    outline: none;
}
.sp-track::-webkit-scrollbar {
    display: none;
}
.sp-track:focus-visible {
    box-shadow: inset 0 0 0 2px var(--agent-accent-ring, rgb(251 76 31 / 0.35));
    border-radius: 12px;
}
.sp-page {
    flex: 0 0 100%;
    min-width: 0;
    scroll-snap-align: start;
    scroll-snap-stop: always;
}
.sp-dots {
    display: flex;
    justify-content: center;
    gap: 2px;
    padding: 6px 0 2px;
}
.sp-dot {
    display: grid;
    place-items: center;
    width: 20px;
    height: 24px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.sp-dot span {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: var(--lh-text-5, #d4d4d4);
    transition:
        background-color var(--lh-dur-fast, 150ms) var(--lh-ease-out, ease-out),
        transform var(--lh-dur-fast, 150ms) var(--lh-spring-soft, ease-out);
}
.sp-dot.is-on span {
    background: var(--lh-text-1, #242424);
    transform: scale(1.15);
}
@media (prefers-reduced-motion: reduce) {
    .sp-track {
        scroll-behavior: auto;
    }
}
</style>
